import { Reveal, SectionHeading, SectionCta } from "./Reveal";

const LAYERS = [
  {
    num: "01",
    title: "Coverage",
    copy: "Every call, DM and web inquiry answered and qualified, not just picked up. A dedicated HIPAA-aware coordinator, plus cross-trained backup, so the desk never goes quiet when one person is out.",
    img: "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjYWxsJTIwY2VudGVyJTIwd29tYW4lMjBzbWlsZSUyMGJyaWdodHxlbnwwfHx8fDE3OTIxMDAyODd8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
    alt: "Smiling coordinator answering calls with a headset in a bright office",
  },
  {
    num: "02",
    title: "Pipeline",
    copy: "Your CRM run as a revenue tool instead of a parking lot for cold inquiries. Every lead staged, every follow-up scheduled, nothing left sitting untouched.",
    img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxsYXB0b3AlMjBkYXNoYm9hcmQlMjBjbGVhbiUyMGRlc2t8ZW58MHx8fHwxNzkyMTAwMjcyfDA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
    alt: "Laptop on a clean desk running the practice pipeline",
  },
  {
    num: "03",
    title: "Follow-up and booking",
    copy: "Inquiries worked until they book or opt out. Consults offered, deposits collected to your policy, confirmations sent, no-shows chased. First response in under five minutes.",
    img: "https://images.unsplash.com/photo-1783099779526-447af3eb79c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxjYWxlbmRhciUyMHBsYW5uZXIlMjBwZW4lMjBkZXNrfGVufDB8fHx8MTc5MjEwMDI3Mnww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
    alt: "Calendar and pen booking a consultation slot",
  },
  {
    num: "04",
    title: "Financial visibility",
    copy: "Reporting delivered to you: inquiry volume, answer rate, speed-to-lead, booked consults, show rate, pipeline value by stage. Numbers instead of gut feelings. No dashboard left for you to interpret.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjByZXBvcnQlMjBjaGFydHMlMjBkZXNrfGVufDB8fHx8MTc5MjEwMDI3M3ww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
    alt: "Financial charts and KPI report on a laptop screen",
  },
  {
    num: "05",
    title: "Account management",
    copy: "A named professional who owns your relationship, reviews recorded calls weekly, watches the KPI scorecard, and is personally accountable for your result. This is the layer everyone else skips, and the reason the whole thing holds.",
    img: "https://images.unsplash.com/photo-1758691737246-95bf8f09a997?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHx0d28lMjBwcm9mZXNzaW9uYWxzJTIwdGFsa2luZyUyMGJyaWdodCUyMG9mZmljZXxlbnwwfHx8fDE3OTIxMDAyODd8MA&ixlib=rb-4.1.0&q=70&w=1600&auto=format",
    alt: "Account manager in a weekly review meeting with a practice owner",
  },
];

const Card = ({ layer, className = "" }) => (
  <div
    data-testid={`mechanism-layer-${layer.num}`}
    className={`group relative border border-black/10 bg-obsidian p-8 md:p-10 shadow-[0_2px_16px_rgba(14,20,27,0.05)] transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-champagne/50 hover:shadow-[0_20px_60px_rgba(61,165,221,0.12)] ${className}`}
  >
    <div className="flex items-baseline gap-4">
      <span className="font-mono text-sm text-champagne">{layer.num}</span>
      <span className="h-px flex-1 bg-black/10 group-hover:bg-champagne/40 transition-colors duration-500" />
    </div>
    <h3 className="mt-6 font-display text-3xl md:text-4xl font-light text-pearl">{layer.title}</h3>
    <p className="mt-4 text-sm md:text-base text-zinc-500 leading-relaxed max-w-lg">{layer.copy}</p>
    <div className="mt-8 clipped-frame overflow-hidden">
      <img
        src={layer.img}
        alt={layer.alt}
        loading="lazy"
        decoding="async"
        className="w-full h-44 object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-700"
      />
    </div>
  </div>
);

export const Mechanism = () => (
  <section id="engine" data-testid="mechanism-section" className="py-24 md:py-36 bg-coal border-y border-black/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading
        eyebrow="The mechanism"
        title={
          <>
            The Back-Office Engine<span className="text-champagne">™</span>
          </>
        }
        copy="Not a virtual assistant you have to manage. Not an answering service that takes a message. A complete back-office function installed inside your practice, in your systems, in your voice. Five layers running at once."
      />

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
        <Reveal className="md:col-span-12 lg:col-span-7">
          <Card layer={LAYERS[0]} className="h-full" />
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-6 lg:col-span-5">
          <Card layer={LAYERS[1]} className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-6 lg:col-span-5">
          <Card layer={LAYERS[2]} className="h-full" />
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-12 lg:col-span-7">
          <Card layer={LAYERS[3]} className="h-full" />
        </Reveal>
        <Reveal className="md:col-span-12">
          <Card layer={LAYERS[4]} />
        </Reveal>
      </div>

      <SectionCta id="mechanism" label="Install the Engine in my practice" />
    </div>
  </section>
);
